import { Loader, SearchableSelect } from 'components';
import { TextField, ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useSelectedLocation } from 'contexts';
import { useCarrierAPI } from 'hooks';
import {
  createContext,
  Dispatch,
  FC,
  FormEventHandler,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { validateScac } from 'services';
import { CarrierContact, TruckingCompany } from 'types';
import { mapById } from 'utils';

import { NOTES_MAX_LENGTH, USDOT_MAX_LENGTH, USDOT_PATTERN } from '../carrier-form.constants';

type NewCarrierContextType = {
  carrier: Partial<TruckingCompany>;
  formRef: RefObject<HTMLFormElement>;
  setCarrier: Dispatch<SetStateAction<Partial<TruckingCompany>>>;
  setContacts: (contact?: CarrierContact, index?: number) => void;
  handleReset: FormEventHandler<HTMLFormElement>;
  handleSubmit: FormEventHandler<HTMLFormElement>;
};

const NewCarrierContext = createContext<NewCarrierContextType>({
  carrier: {},
  formRef: { current: null },
  setCarrier: () => {},
  setContacts: () => {},
  handleReset: () => {},
  handleSubmit: () => {}
});

const useNewCarrier = () => useContext(NewCarrierContext);

const Contact: FC<{ contact: CarrierContact; index: number }> = ({ contact, index }) => {
  const { carrier, setContacts } = useNewCarrier();
  const { locations } = useSelectedLocation();

  const locationMap = useMemo(() => mapById(locations ?? []), [locations]);

  const handleChange = useCallback(
    (partial: Partial<CarrierContact>) => {
      setContacts({ ...contact, ...partial }, index);
    },
    [contact, index, setContacts]
  );

  const handleDelete = useCallback(() => {
    setContacts(undefined, index);
  }, [index, setContacts]);

  return (
    <div className="grid-4 p-3 border-bottom">
      <div>
        <h6 className="text-info-dark">Location</h6>
        <div className="mt-2 w-100">
          <SearchableSelect
            onValueSelected={id => {
              handleChange({ location: locationMap.get(Number(id)) });
            }}
            options={[...locationMap.values()].map(({ id, name }) => ({ label: name, value: `${id}` }))}
            value={`${contact.location?.id ?? ''}`}
            required
            data-cy={`contact-${index}-location-input`}
          />
        </div>
      </div>
      <div>
        <h6 className="text-info-dark">Email(s)</h6>
        <input
          type="text"
          className="mt-2 w-100"
          onChange={e => handleChange({ email: e.target.value })}
          value={contact.email ?? ''}
          required
          data-cy={`contact-${index}-email-input`}
        />
      </div>
      <div className="flex-fill d-flex align-items-end h-100">
        <button
          className="btn-back p-2"
          type="button"
          onClick={handleDelete}
          disabled={carrier.contacts?.length === 0}
          data-cy={`contact-${index}-delete-button`}
        >
          <span className="material-icons icon-xlarge">delete</span>
        </button>
      </div>
    </div>
  );
};

export const NewCarrier: FC = () => {
  const navigate = useNavigate();
  const { createCarrier } = useCarrierAPI();

  const [carrier, setCarrier] = useState<Partial<TruckingCompany>>({});

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  const setContacts = (contact?: CarrierContact, index?: number) => {
    setCarrier(previous => {
      const contacts = Object.assign([], previous.contacts);
      if (!contact) {
        contacts.splice(index ?? contacts.length, 1);
      } else {
        contacts.splice(index ?? contacts.length, 1, contact);
      }
      return { ...previous, contacts };
    });
  };

  const handleReset: FormEventHandler<HTMLFormElement> = useCallback(() => {
    navigate('/scac-management', { replace: true });
  }, [navigate]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      setIsSubmitting(true);

      if (!carrier || !carrier.scacCode || !carrier.name) {
        setIsSubmitting(false);
        return;
      }

      if ((carrier.notes?.length ?? 0) > NOTES_MAX_LENGTH) {
        setIsSubmitting(false);
        return;
      }

      if (carrier.usdotNumber && !new RegExp(`^${USDOT_PATTERN}$`).test(carrier.usdotNumber)) {
        setIsSubmitting(false);
        return;
      }

      const response = await createCarrier({
        name: carrier.name,
        scacCode: carrier.scacCode,
        contacts: carrier.contacts ?? [],
        notes: carrier.notes,
        usdotNumber: carrier.usdotNumber
      });

      setIsSubmitting(false);
      if (response) {
        navigate(`/scac-management/carriers/${response.id}`, { replace: true });
      }
    },
    [carrier, createCarrier, navigate]
  );

  const validateScacInput = useCallback(async (scac: string): Promise<ValidationResult> => {
    if (!scac) {
      return { status: ValidationStatus.DEFAULT };
    }

    const isValid = await validateScac(scac);
    if (isValid) {
      return { status: ValidationStatus.SUCCESS };
    }

    return { status: ValidationStatus.WARNING, error: 'SCAC code not available.' };
  }, []);

  return (
    <NewCarrierContext.Provider
      value={{
        carrier,
        formRef,
        setCarrier,
        setContacts,
        handleReset,
        handleSubmit
      }}
    >
      <form
        className="page"
        id="new-carrier"
        onReset={handleReset}
        onSubmit={handleSubmit}
        ref={formRef}
        data-cy="new-carrier-form"
      >
        <div className="page-header">
          <h1>NEW CARRIER</h1>
          <div className="d-flex g-3 flex-row-reverse">
            <Loader loading={isSubmitting}>
              <button className="btn-primary" type="submit">
                SUBMIT
              </button>
            </Loader>
            <button className="btn-outline" type="reset">
              CANCEL
            </button>
          </div>
        </div>
        <div className="page-content flex-fill">
          <div className="card-primary">
            <div className="card-header px-3">
              <h4 className="text-uppercase">Carrier Information</h4>
            </div>
            <div className="card-body">
              <div className="grid-4 p-2">
                <div>
                  <h6 className="text-info-dark">Name</h6>
                  <input
                    type="text"
                    className="mt-2 w-100"
                    defaultValue={carrier.name ?? ''}
                    onChange={e => setCarrier(c => c && { ...c, name: e.target.value })}
                    required
                    data-cy="new-carrier-name-input"
                  />
                </div>
                <div>
                  <h6 className="text-info-dark">SCAC Code</h6>
                  <TextField
                    className="mt-2"
                    value={carrier.scacCode ?? ''}
                    onChange={scacCode => setCarrier(c => c && { ...c, scacCode })}
                    validate={validateScacInput}
                    required
                    data-cy="new-carrier-scac-code-input"
                  />
                </div>
                <div>
                  <h6 className="text-info-dark">USDOT Number</h6>
                  <input
                    type="text"
                    className="mt-2 w-100"
                    value={carrier.usdotNumber ?? ''}
                    onChange={e => setCarrier(c => c && { ...c, usdotNumber: e.target.value })}
                    pattern={USDOT_PATTERN}
                    maxLength={USDOT_MAX_LENGTH}
                    title="USDOT must be 8–10 alphanumeric characters"
                    data-cy="new-carrier-usdot-number-input"
                  />
                </div>
              </div>
              <div className="grid-2 p-2">
                <div>
                  <h6 className="text-info-dark">Notes</h6>
                  <textarea
                    className="mt-2 w-100"
                    rows={3}
                    maxLength={NOTES_MAX_LENGTH}
                    value={carrier.notes ?? ''}
                    onChange={e => setCarrier(c => c && { ...c, notes: e.target.value })}
                    data-cy="new-carrier-notes-input"
                  />
                  <small
                    className={(carrier.notes?.length ?? 0) >= NOTES_MAX_LENGTH ? 'text-danger' : 'text-info-dark'}
                  >
                    {carrier.notes?.length ?? 0}/{NOTES_MAX_LENGTH}
                  </small>
                </div>
              </div>
            </div>
          </div>
          <div className="card-primary">
            <div className="card-header px-3">
              <h4 className="text-uppercase">Contact(s)</h4>
            </div>
          </div>
          <div className="card-body">
            <div className="p-2">
              {carrier.contacts?.map((contact, index) => (
                <Contact contact={contact} index={index} key={`contact-${index}`} />
              ))}
              <div className="p-2">
                <button
                  className="btn-outline"
                  type="button"
                  onClick={() => setContacts({} as CarrierContact)}
                  data-cy="new-carrier-add-contact-button"
                >
                  Add Contact
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
      <ErrorDialog />
    </NewCarrierContext.Provider>
  );
};
